import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { getAssignedParkingLotHistory, getEmployee } from '../../services/employeeServices';
import './EmployeeParkingLots.css';

const EmployeeParkingLots = () => {
  const user = useSelector(state => state.user);
  const [loading, setLoading] = useState(true);
  const [parkingLots, setParkingLots] = useState([]);
  const [error, setError] = useState('');
  const [openHistoryLotId, setOpenHistoryLotId] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyError, setHistoryError] = useState('');

  const employeeCode = useMemo(() => user?.userData?.userCode
    || user?.userData?.userData?.userCode
    || user?.account?.userCode
    || '', [user]);

  useEffect(() => {
    let active = true;
    const load = async () => {
      if (!employeeCode) {
        setError('Không xác định được mã nhân viên.');
        setLoading(false);
        return;
      }
      try {
        const response = await getEmployee(employeeCode);
        if (!active) return;
        if (+!response?.ok) {
          setError(response?.message || 'Không thể tải bãi xe phụ trách.');
          return;
        }
        const lot = response?.data?.userWithParkingLot?.ParkingLot;
        setParkingLots(lot ? [lot] : []);
      } catch (_) {
        if (active) setError('Không thể kết nối tới máy chủ để tải bãi xe.');
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => { active = false; };
  }, [employeeCode]);

  const toggleHistory = async (parkingLotId) => {
    if (openHistoryLotId === parkingLotId) {
      setOpenHistoryLotId(null);
      return;
    }

    setOpenHistoryLotId(parkingLotId);
    setHistory([]);
    setHistoryError('');
    setHistoryLoading(true);
    try {
      const response = await getAssignedParkingLotHistory(employeeCode, parkingLotId);
      if (response?.ok) setHistory(Array.isArray(response.data) ? response.data : []);
      else setHistoryError(response?.message || 'Không thể tải lịch sử ra vào.');
    } catch (requestError) {
      setHistoryError(requestError?.response?.message || 'Không thể tải lịch sử ra vào.');
    } finally {
      setHistoryLoading(false);
    }
  };

  const formatDateTime = value => value ? new Date(value).toLocaleString('vi-VN') : 'Đang trong bãi';

  return (
    <main className="employee-lots-page">
      <section className="employee-lots-container">
        <div className="employee-lots-heading">
          <div><span className="employee-lots-eyebrow">Phân công nhân viên</span><h1>Các bãi xe phụ trách</h1><p>Thông tin khu vực bãi xe được quản trị viên phân công cho bạn.</p></div>
          <div className="employee-code-badge"><i className="bi bi-person-badge"></i><span>{employeeCode || '---'}</span></div>
        </div>
        {loading && <div className="employee-lots-state"><div className="spinner-border text-primary" role="status"></div><span>Đang tải thông tin bãi xe...</span></div>}
        {!loading && error && <div className="employee-lots-state employee-lots-error"><i className="bi bi-exclamation-circle"></i><span>{error}</span></div>}
        {!loading && !error && parkingLots.length === 0 && <div className="employee-lots-state"><i className="bi bi-p-circle"></i><span>Bạn chưa được phân công phụ trách bãi xe nào.</span></div>}
        {!loading && !error && parkingLots.map(lot => {
          const available = Number(lot.capacity || 0);
          const isAvailable = available > 0 && String(lot.status || '').toLowerCase() !== 'hết chỗ';
          return <article className="employee-lot-card" key={lot.parkingLotId}>
            <div className="employee-lot-summary"><div className="employee-lot-icon"><i className="bi bi-building"></i></div>
            <div className="employee-lot-content">
              <div className="employee-lot-title-row"><div><span className="employee-lot-id">Mã bãi #{lot.parkingLotId}</span><h2>{lot.parkingLotName || `Bãi xe ${lot.parkingLotId}`}</h2></div><span className={`employee-lot-status ${isAvailable ? 'available' : 'full'}`}>{lot.status || (isAvailable ? 'Còn chỗ' : 'Hết chỗ')}</span></div>
              <p className="employee-lot-description">{lot.description || 'Chưa có mô tả cho bãi xe này.'}</p>
              <div className="employee-lot-metrics"><div><i className="bi bi-p-square"></i><span>Chỗ còn trống</span><strong>{available}</strong></div><div><i className="bi bi-shield-check"></i><span>Phụ trách bởi</span><strong>{employeeCode}</strong></div></div>
              <button type="button" className="employee-history-button" onClick={() => toggleHistory(lot.parkingLotId)}>
                <i className="bi bi-clock-history"></i>{openHistoryLotId === lot.parkingLotId ? 'Ẩn lịch sử ra vào' : 'Xem lịch sử ra vào'}
              </button>
            </div></div>
            {openHistoryLotId === lot.parkingLotId && <section className="employee-history-panel">
              <div className="employee-history-heading"><div><h3>Lịch sử xe ra vào</h3><p>{lot.parkingLotName || `Bãi xe ${lot.parkingLotId}`}</p></div><span>{history.length} lượt</span></div>
              {historyLoading && <div className="employee-history-state"><div className="spinner-border spinner-border-sm text-primary"></div>Đang tải lịch sử...</div>}
              {!historyLoading && historyError && <div className="employee-history-state error">{historyError}</div>}
              {!historyLoading && !historyError && history.length === 0 && <div className="employee-history-state">Bãi xe chưa có lịch sử ra vào.</div>}
              {!historyLoading && !historyError && history.length > 0 && <div className="employee-history-table-wrap"><table className="employee-history-table">
                <thead><tr><th>Biển số</th><th>Người dùng</th><th>Loại xe</th><th>Thời gian vào</th><th>Thời gian ra</th><th>Phí</th><th>Trạng thái</th></tr></thead>
                <tbody>{history.map((item, index) => <tr key={`${item.userCode}-${item.timeIn}-${index}`}><td><strong>{item.vehicle?.plate || '---'}</strong></td><td>{item.userCode || 'Khách vãng lai'}</td><td>{item.vehicle?.type || '---'}</td><td>{formatDateTime(item.timeIn)}</td><td>{formatDateTime(item.timeOut)}</td><td>{Number(item.cost || 0).toLocaleString('vi-VN')} đ</td><td><span className={`history-status ${item.timeOut ? 'out' : 'in'}`}>{item.timeOut ? 'Đã ra' : 'Trong bãi'}</span></td></tr>)}</tbody>
              </table></div>}
            </section>}
          </article>;
        })}
      </section>
    </main>
  );
};

export default EmployeeParkingLots;
